// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Huntress — the static markup. Kept out of main.ts so the screen machine stays readable, and so
// the help copy sits next to the About copy that has to stay honest about the same things.

import { escapeHtml } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { TIERS, tierOf } from './bot';
import type { ResultRow } from './duel';
import type { Verdict } from './analysis';
import type { WinReason } from './rules';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export function menuHtml(o: { modeId: string; tierId: string; record: [number, number]; muted: boolean }): string {
  const mode = modeOf(o.modeId);
  const tier = tierOf(o.tierId);
  return `
  <section class="screen menu">
    <h1>Huntress</h1>
    <p class="tagline">Run her like a queen, then loose an arrow. The square it lands on burns for good — and the last hunter who can still move wins.</p>

    <div class="pickers">
      <span class="picker-label">Ground</span>
      <div class="picker-row">
        ${MODE_IDS.map(
          (id) =>
            `<button class="chip${id === o.modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === o.modeId}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}
      </div>
      <p class="blurb">${escapeHtml(mode.blurb)}</p>

      <span class="picker-label">Opponent</span>
      <div class="picker-row">
        ${TIERS.map(
          (t) =>
            `<button class="chip${t.id === o.tierId ? ' on' : ''}" data-tier="${t.id}" aria-pressed="${t.id === o.tierId}">${escapeHtml(t.name)}</button>`,
        ).join('')}
      </div>
      <p class="blurb">${escapeHtml(tier.blurb)}</p>
    </div>

    <p class="record">${o.record[0]}&nbsp;won · ${o.record[1]}&nbsp;lost on ${escapeHtml(mode.name)}</p>
  </section>
  <div class="menu-actions">
    <button class="btn primary" id="playSolo">Play</button>
    <button class="btn" id="playFriends">Play with a friend</button>
    <button class="btn ghost" id="help">How to play</button>
    <button class="btn ghost" id="about">About</button>
    <button class="iconbtn" id="mute" aria-label="${o.muted ? 'Unmute' : 'Mute'}">${o.muted ? '🔇' : '🔊'}</button>
  </div>`;
}

export function helpHtml(): string {
  return `
  <button class="btn ghost sheet-close" aria-label="Close">✕</button>
  <h2>How to play</h2>
  <ol>
    <li><strong>Run.</strong> Tap one of your huntresses, then a marked square. She moves like a chess queen — any distance in a straight or diagonal line, through nothing.</li>
    <li><strong>Shoot.</strong> From where she landed, tap a square along another clear line. It burns, and it is gone for the rest of the game.</li>
    <li><strong>Win.</strong> Nobody is ever captured. You win when your opponent has no legal move left — so the game is about fencing them into a smaller and smaller patch of ground while keeping room for yourself.</li>
  </ol>
  <p>The square she came from is empty behind her, so she can shoot the doorway shut. That one trick is most of the game.</p>
  <p>When neither side can reach the other, the rest is bookkeeping — so the round is called there, for whoever has more moves left in their own ground.</p>`;
}

export function aboutHtml(): string {
  return `
  <button class="btn ghost sheet-close" aria-label="Close">✕</button>
  <h2>About Huntress</h2>
  <p>A two-player abstract on an ever-shrinking board. Every turn removes one square from the world, so the game is a slow argument about who gets the last of the ground. There is no luck once the board is dealt, and no hidden information — only the shape you leave behind.</p>
  <p>The solo opponent is a real search engine: it looks several turns ahead and counts the ground each side can reach first. Three tiers, from one that keeps its options open to one that will fence you in without ever touching you.</p>
  <p>Play with a friend over a room link, a code or a QR: the connection is <strong>peer to peer</strong>, browser to browser. A public signalling relay introduces the two browsers and then steps out of the way — no game server, and no game state on anyone's machine but yours.</p>
  <p>No cookies, no accounts, no tracking. Anonymous, cookie-less page-view counts via Cloudflare Web Analytics. All the art and sound is generated in the page.</p>
  <p>${ATTRIB}</p>`;
}

const REASON: Record<WinReason, string> = {
  sealed: 'sealed in — no move left',
  territory: 'the ground was already decided',
  walkover: 'the other hunter left',
  nocontest: 'nobody played a turn',
  none: '',
};

export function resultsHtml(o: {
  rows: ResultRow[];
  mode: Mode;
  reason: WinReason;
  winner: number;
  tally: [number, number];
  /** Who the two tally numbers belong to — "You" and the opponent, not the two seat colours. */
  tallyLabels: [string, string];
  round: number;
  fault: 'desync' | 'build' | null;
}): string {
  const head =
    o.fault === 'build'
      ? 'Different builds'
      : o.fault === 'desync'
        ? 'Round stopped'
        : o.winner < 0
          ? 'No contest'
          : `${escapeHtml(o.rows[o.winner].name)} wins`;
  const sub =
    o.fault === 'build'
      ? 'The two of you are on different versions — reload and play again.'
      : o.fault === 'desync'
        ? 'The two boards stopped agreeing, so the round was stopped.'
        : REASON[o.reason] || '';

  const rows = o.rows
    .map((r) => {
      const shape = r.side === 0 ? '' : ' p2';
      return `
      <div class="row${r.won ? ' win' : ''}">
        <span class="rtok${shape}"></span>
        <span class="row-name">${escapeHtml(r.name)}${r.isSelf ? ' (you)' : ''}${r.isBot ? ' · bot' : ''}${r.left ? ' · left' : ''}</span>
        <span>${r.won ? '🏹' : ''}</span>
        <span class="row-stats">
          <span><b>${r.land}</b> squares held</span>
          <span><b>${r.turns}</b> turns</span>
          <span><b>${r.mobility.toFixed(0)}</b> avg. moves open</span>
          <span><b>${r.doors}</b> doors shut</span>
          <span><b>${r.pins}</b> arrows at their heels</span>
        </span>
      </div>`;
    })
    .join('');

  return `
    <div class="result-head">
      <h2>${head}</h2>
      <p class="result-sub">${escapeHtml(sub)}</p>
    </div>
    <canvas class="finalboard" id="finalBoard" width="680" height="680" aria-label="the ground each hunter held at the end"></canvas>
    <p class="result-sub">Shaded ground is what each hunter could reach first. Charred squares belong to nobody.</p>
    <div class="rows">${rows}</div>
    <div id="verdict"></div>
    <p class="tally">${escapeHtml(o.tallyLabels[0])} ${o.tally[0]} — ${o.tally[1]} ${escapeHtml(o.tallyLabels[1])} · round ${o.round} · ${escapeHtml(o.mode.name)}</p>`;
}

export function verdictHtml(v: Verdict | null): string {
  if (!v) return '';
  return `<p class="result-sub">${escapeHtml(v.isSelf ? 'Your' : `${v.name}'s`)} hunt turned on turn <b>${v.turn}</b>: <b>${escapeHtml(v.played)}</b> gave up ${v.drop} points of ground — <b>${escapeHtml(v.better)}</b> held it.</p>`;
}
